import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Calendar, Plus, Edit2, Trash2, Save, X, ChevronDown, ChevronRight,
  Users, BarChart3, Settings, FileText, Clock, CheckCircle2, AlertTriangle,
  RefreshCw, Download, Search, MoreVertical, Loader2,
  CalendarDays, Sparkles, Flag, Home, Heart, Coffee, Baby, User as UserIcon,
  MinusCircle, HeartPulse, Eye, ArrowUpDown
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import JoinedLeaveReport from './leaves/JoinedLeaveReport';
import { Link2 } from 'lucide-react';
import { Progress } from './ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

const BACKEND_URL = '';

const adminApi = axios.create({ baseURL: BACKEND_URL, timeout: 30000 });
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Icon mapping for leave types
const leaveIcons = {
  'palm-tree': CalendarDays,
  'heart-pulse': HeartPulse,
  'coffee': Coffee,
  'flag': Flag,
  'sparkles': Sparkles,
  'refresh-cw': RefreshCw,
  'home': Home,
  'baby': Baby,
  'user': UserIcon,
  'heart': Heart,
  'minus-circle': MinusCircle,
  'calendar': CalendarDays
};

// Color scheme matching design guidelines
const policyColors = {
  'pl': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', accent: '#10B981' },
  'sl': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', accent: '#EF4444' },
  'cl': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', accent: '#F59E0B' },
  'ml': { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', accent: '#6366F1' },
  'ol': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', accent: '#8B5CF6' },
  'comp': { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', accent: '#06B6D4' },
  'wfh': { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', accent: '#EC4899' },
  'lop': { bg: 'bg-white/40', text: 'text-neutral-700', border: 'border-white/40', accent: '#64748B' },
  'default': { bg: 'bg-white/40', text: 'text-neutral-700', border: 'border-white/40', accent: '#64748B' }
};

export default function AdminLeaveManagement() {
  const [activeTab, setActiveTab] = useState('policies');
  const [loading, setLoading] = useState(true);
  
  // Leave Policies State
  const [policies, setPolicies] = useState([]);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  
  // Holiday Calendar State
  const [holidayYear, setHolidayYear] = useState(2026);
  const [mandatoryHolidays, setMandatoryHolidays] = useState([]);
  const [optionalHolidays, setOptionalHolidays] = useState([]);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayType, setHolidayType] = useState('optional');
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', data: null });
  
  // Inventory State
  const [inventory, setInventory] = useState(null);
  const [employeeBalances, setEmployeeBalances] = useState({ policies: [], employees: [] });
  
  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const fetchPolicies = useCallback(async () => {
    try {
      const res = await adminApi.get('/api/admin/leave-policies/all');
      setPolicies(res.data);
    } catch (error) {
      toast.error('Failed to load leave policies');
    }
  }, []);

  const fetchHolidayCalendar = useCallback(async (year) => {
    try {
      const res = await adminApi.get(`/api/admin/holiday-calendars/${year}`);
      const byDate = (list) => [...(list || [])].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      setMandatoryHolidays(byDate(res.data.mandatory?.holidays));
      setOptionalHolidays(byDate(res.data.optional?.holidays));
    } catch (error) {
      toast.error('Failed to load holiday calendar');
    }
  }, []);

  const fetchInventory = useCallback(async () => {
    try {
      const [invRes, balRes] = await Promise.all([
        adminApi.get('/api/admin/leave-inventory'),
        adminApi.get('/api/admin/leave-inventory/employees')
      ]);
      setInventory(invRes.data);
      setEmployeeBalances(balRes.data);
    } catch (error) {
      console.error('Failed to load inventory:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchPolicies(),
        fetchHolidayCalendar(holidayYear),
        fetchInventory()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchPolicies, fetchHolidayCalendar, fetchInventory, holidayYear]);

  // Policy CRUD
  const handleSavePolicy = async (policyData) => {
    try {
      if (editingPolicy) {
        await adminApi.put(`/api/admin/leave-policies/${editingPolicy.code}`, policyData);
        toast.success('Leave policy updated successfully');
      } else {
        await adminApi.post('/api/admin/leave-policies', policyData);
        toast.success('Leave policy created successfully');
      }
      setShowPolicyModal(false);
      setEditingPolicy(null);
      fetchPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save policy');
    }
  };

  const handleDeletePolicy = async () => {
    if (!deleteConfirm.data) return;
    try {
      await adminApi.delete(`/api/admin/leave-policies/${deleteConfirm.data}`);
      toast.success('Leave policy deactivated');
      fetchPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete policy');
    } finally {
      setDeleteConfirm({ open: false, type: '', data: null });
    }
  };

  // Holiday CRUD - FIXED
  const handleSaveHoliday = async (holidayData) => {
    try {
      if (editingHoliday) {
        await adminApi.put(
          `/api/admin/holiday-calendars/${holidayYear}/${editingHoliday.date}?holiday_type=${editingHoliday.type}`,
          { date: holidayData.date, name: holidayData.name }
        );
        toast.success('Holiday updated successfully');
      } else {
        await adminApi.post(
          `/api/admin/holiday-calendars/${holidayYear}/add-holiday?holiday_type=${holidayType}`,
          holidayData
        );
        toast.success('Holiday added successfully');
      }
      setShowHolidayModal(false);
      setEditingHoliday(null);
      fetchHolidayCalendar(holidayYear);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save holiday');
    }
  };

  const openEditHoliday = (holiday, type) => {
    setEditingHoliday({ date: holiday.date, name: holiday.name, day: holiday.day, type });
    setShowHolidayModal(true);
  };

  // FIXED: Delete holiday with correct parameters
  const handleDeleteHoliday = async () => {
    if (!deleteConfirm.data) return;
    const { date, type } = deleteConfirm.data;
    try {
      await adminApi.delete(
        `/api/admin/holiday-calendars/${holidayYear}/${date}?holiday_type=${type}`
      );
      toast.success('Holiday removed successfully');
      fetchHolidayCalendar(holidayYear);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove holiday');
    } finally {
      setDeleteConfirm({ open: false, type: '', data: null });
    }
  };

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get color scheme for policy
  const getColorScheme = (code) => policyColors[code] || policyColors.default;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="admin-leave-management-loading">
        {/* Header skeleton */}
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="h-6 w-56 rounded-md bg-neutral-200 animate-pulse" />
            <div className="h-3 w-96 rounded-md bg-neutral-100 animate-pulse" />
          </div>
        </div>
        {/* Card grid skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map(i => (
            <div key={i} className="glass-card p-5 space-y-3">
              <div className="h-8 w-8 rounded-lg bg-neutral-200 animate-pulse" />
              <div className="h-3 w-24 rounded bg-neutral-200 animate-pulse" />
              <div className="h-7 w-16 rounded bg-neutral-300 animate-pulse" />
            </div>
          ))}
        </div>
        {/* Table skeleton */}
        <div className="glass-card p-5 space-y-2">
          <div className="h-4 w-32 rounded bg-neutral-200 animate-pulse mb-4" />
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="flex items-center gap-4 py-2">
              <div className="h-4 w-24 rounded bg-neutral-100 animate-pulse" />
              <div className="h-4 w-16 rounded bg-neutral-100 animate-pulse" />
              <div className="h-4 flex-1 rounded bg-neutral-100 animate-pulse" />
              <div className="h-4 w-8 rounded bg-neutral-100 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-leave-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#1a1a2e]">Leave Management</h1>
          <p className="text-neutral-600 text-sm mt-1">Manage leave policies, holiday calendars, and employee allocations</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-neutral-100 p-1 rounded-xl inline-flex">
          <TabsTrigger 
            value="policies" 
            className="rounded-xl px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-[#1a1a2e] data-[state=active]:shadow-soft"
          >
            <Settings className="w-4 h-4 mr-2" />
            Leave Policies
          </TabsTrigger>
          <TabsTrigger 
            value="holidays" 
            className="rounded-xl px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-[#1a1a2e] data-[state=active]:shadow-soft"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Holiday Calendar
          </TabsTrigger>
          <TabsTrigger 
            value="inventory" 
            className="rounded-xl px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-[#1a1a2e] data-[state=active]:shadow-soft"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Leave Inventory
          </TabsTrigger>
          <TabsTrigger 
            value="allocations" 
            className="rounded-xl px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-[#1a1a2e] data-[state=active]:shadow-soft"
          >
            <Users className="w-4 h-4 mr-2" />
            Employee Balances
          </TabsTrigger>
          <TabsTrigger 
            value="joined" 
            data-testid="leave-tab-joined"
            className="rounded-xl px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-[#1a1a2e] data-[state=active]:shadow-soft"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Joined Leaves
          </TabsTrigger>
        </TabsList>

        <TabsContent value="joined" className="space-y-4">
          {activeTab === 'joined' && <JoinedLeaveReport api={adminApi} />}
        </TabsContent>

        {/* ==================== LEAVE POLICIES TAB ==================== */}
        <TabsContent value="policies" className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="Search policies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button 
              onClick={() => { setEditingPolicy(null); setShowPolicyModal(true); }}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Leave Type
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {policies
              .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          p.code.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((policy) => {
                const IconComponent = leaveIcons[policy.icon] || CalendarDays;
                const colors = getColorScheme(policy.code);
                const usagePercent = policy.usage_stats?.total_days_used && policy.annual_allocation > 0
                  ? Math.min(100, Math.round((policy.usage_stats.total_days_used / (policy.annual_allocation * 10)) * 100))
                  : 0;

                return (
                  <div
                    key={policy._id || policy.id || policy.code}
                    className={`bg-white rounded-xl border ${policy.is_active ? 'border-white/40' : 'border-red-200'} p-5 hover:shadow-soft transition-shadow`}
                    data-testid={`policy-card-${policy.code}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center`}>
                          <IconComponent className={`w-5 h-5 ${colors.text}`} />
                        </div>
                        <div>
                          <h3 className="font-medium text-[#1a1a2e]">{policy.name}</h3>
                          <p className="text-xs text-neutral-500 uppercase tracking-wide">{policy.code}</p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4 text-neutral-500" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem onClick={() => { setEditingPolicy(policy); setShowPolicyModal(true); }}>
                            <Edit2 className="w-4 h-4 mr-2" /> Edit Policy
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => setDeleteConfirm({ open: true, type: 'policy', data: policy.code })}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Deactivate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <p className="text-sm text-neutral-600 mb-4 line-clamp-2">{policy.description}</p>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-white/40 rounded-xl p-3">
                        <p className="text-xs text-neutral-500 mb-0.5">Annual Days</p>
                        <p className="text-xl font-semibold text-[#1a1a2e]">{policy.annual_allocation}</p>
                      </div>
                      <div className="bg-white/40 rounded-xl p-3">
                        <p className="text-xs text-neutral-500 mb-0.5">Pending</p>
                        <p className={`text-xl font-semibold ${policy.pending_requests > 0 ? 'text-amber-600' : 'text-neutral-400'}`}>
                          {policy.pending_requests || 0}
                        </p>
                      </div>
                    </div>

                    {policy.annual_allocation > 0 && (
                      <div className="space-y-1.5 mb-4">
                        <div className="flex justify-between text-xs">
                          <span className="text-neutral-500">Usage this year</span>
                          <span className="font-medium text-neutral-700">{policy.usage_stats?.total_days_used || 0} days</span>
                        </div>
                        <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all"
                            style={{ width: `${usagePercent}%`, backgroundColor: colors.accent }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-1.5">
                      {policy.carry_forward_allowed && (
                        <Badge variant="outline" className="text-xs border-white/40 text-neutral-600">
                          Carry Forward
                        </Badge>
                      )}
                      {policy.encashment_allowed && (
                        <Badge variant="outline" className="text-xs border-white/40 text-neutral-600">
                          Encashable
                        </Badge>
                      )}
                      {policy.requires_approval && (
                        <Badge variant="outline" className="text-xs border-white/40 text-neutral-600">
                          Needs Approval
                        </Badge>
                      )}
                      {!policy.is_active && (
                        <Badge variant="destructive" className="text-xs">Inactive</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </TabsContent>

        {/* ==================== HOLIDAY CALENDAR TAB ==================== */}
        <TabsContent value="holidays" className="space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Select value={holidayYear.toString()} onValueChange={(v) => setHolidayYear(parseInt(v))}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2027">2027</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline" className="text-sm border-white/40">Karnataka State</Badge>
            </div>
            <Button 
              onClick={() => { setEditingHoliday(null); setShowHolidayModal(true); }}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="add-holiday-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Holiday
            </Button>
          </div>

          {/* Mandatory Holidays */}
          <div className="soft-card overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 bg-white/40">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <Flag className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-medium text-[#1a1a2e]">Mandatory Holidays</h3>
                  <p className="text-sm text-neutral-500">{mandatoryHolidays.length} public holidays for {holidayYear}</p>
                </div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-white/40">
                  <TableHead className="w-32">Date</TableHead>
                  <TableHead className="w-28">Day</TableHead>
                  <TableHead>Holiday Name</TableHead>
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mandatoryHolidays.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-sm text-neutral-500">
                      <Calendar className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                      No mandatory holidays added yet
                    </TableCell>
                  </TableRow>
                ) : mandatoryHolidays.map((holiday, idx) => {
                  const isPast = new Date(holiday.date) < new Date();
                  return (
                    <TableRow key={holiday.date} className={`transition-colors hover:bg-neutral-50 ${isPast ? 'bg-white/40 text-neutral-400' : ''}`}>
                      <TableCell className="font-mono text-sm">{holiday.date}</TableCell>
                      <TableCell className="text-sm">{holiday.day}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${isPast ? 'text-neutral-400' : 'text-[#1a1a2e]'}`}>
                            {holiday.name}
                          </span>
                          {isPast && <Badge variant="outline" className="text-xs">Past</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditHoliday(holiday, 'mandatory')}
                          className="h-8 w-8 p-0 text-neutral-400 hover:text-teal-600"
                          data-testid={`edit-holiday-${holiday.date}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm({ 
                            open: true, 
                            type: 'holiday', 
                            data: { date: holiday.date, type: 'mandatory', name: holiday.name }
                          })}
                          className="h-8 w-8 p-0 text-neutral-400 hover:text-red-600"
                          data-testid={`delete-holiday-${holiday.date}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Optional Holidays */}
          <div className="soft-card overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 bg-white/40">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-medium text-[#1a1a2e]">Optional Holidays (Restricted)</h3>
                  <p className="text-sm text-neutral-500">{optionalHolidays.length} festivals - Employees can select up to 5</p>
                </div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-white/40">
                  <TableHead className="w-32">Date</TableHead>
                  <TableHead className="w-28">Day</TableHead>
                  <TableHead>Holiday Name</TableHead>
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {optionalHolidays.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-sm text-neutral-500">
                      <Sparkles className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                      No optional holidays added yet
                    </TableCell>
                  </TableRow>
                ) : optionalHolidays.map((holiday, idx) => {
                  const isPast = new Date(holiday.date) < new Date();
                  const deadlineDate = new Date(holiday.date);
                  deadlineDate.setDate(deadlineDate.getDate() - 12);
                  const isDeadlinePassed = new Date() > deadlineDate && !isPast;
                  
                  return (
                    <TableRow key={holiday.date} className={`transition-colors hover:bg-neutral-50 ${isPast ? 'bg-white/40 text-neutral-400' : ''}`}>
                      <TableCell className="font-mono text-sm">{holiday.date}</TableCell>
                      <TableCell className="text-sm">{holiday.day}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${isPast ? 'text-neutral-400' : 'text-[#1a1a2e]'}`}>
                            {holiday.name}
                          </span>
                          {isPast && <Badge variant="outline" className="text-xs">Past</Badge>}
                          {isDeadlinePassed && !isPast && (
                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-200">
                              Selection Closed
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditHoliday(holiday, 'optional')}
                          className="h-8 w-8 p-0 text-neutral-400 hover:text-teal-600"
                          data-testid={`edit-optional-holiday-${holiday.date}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm({ 
                            open: true, 
                            type: 'holiday', 
                            data: { date: holiday.date, type: 'optional', name: holiday.name }
                          })}
                          className="h-8 w-8 p-0 text-neutral-400 hover:text-red-600"
                          data-testid={`delete-optional-holiday-${holiday.date}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ==================== LEAVE INVENTORY TAB ==================== */}
        <TabsContent value="inventory" className="space-y-6">
          {inventory && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="glass-card p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-neutral-600" />
                    </div>
                  </div>
                  <p className="text-sm text-neutral-500 mb-1">Total Allocated</p>
                  <p className="text-2xl font-semibold text-[#1a1a2e]">{inventory.overall_stats.total_allocated.toLocaleString()}</p>
                  <p className="text-xs text-neutral-400 mt-1">{inventory.total_employees} employees</p>
                </div>
                <div className="glass-card p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                  <p className="text-sm text-neutral-500 mb-1">Total Used</p>
                  <p className="text-2xl font-semibold text-emerald-600">{inventory.overall_stats.total_used}</p>
                  <p className="text-xs text-neutral-400 mt-1">Approved leaves</p>
                </div>
                <div className="glass-card p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                  <p className="text-sm text-neutral-500 mb-1">Pending</p>
                  <p className="text-2xl font-semibold text-amber-600">{inventory.overall_stats.total_pending}</p>
                  <p className="text-xs text-neutral-400 mt-1">Awaiting approval</p>
                </div>
                <div className="glass-card p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-sm text-neutral-500 mb-1">Available</p>
                  <p className="text-2xl font-semibold text-blue-600">{inventory.overall_stats.total_available.toLocaleString()}</p>
                  <p className="text-xs text-neutral-400 mt-1">Remaining balance</p>
                </div>
              </div>

              {/* Policy Breakdown Table */}
              <div className="soft-card overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100">
                  <h3 className="font-medium text-[#1a1a2e]">Leave Type Breakdown - {inventory.year}</h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-white/40">
                      <TableHead>Leave Type</TableHead>
                      <TableHead className="text-right">Per Employee</TableHead>
                      <TableHead className="text-right">Total Allocated</TableHead>
                      <TableHead className="text-right">Used</TableHead>
                      <TableHead className="text-right">Available</TableHead>
                      <TableHead className="w-32">Utilization</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventory.policies_summary.map((policy) => {
                      const colors = getColorScheme(policy.code);
                      return (
                        <TableRow key={policy.code}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: policy.color || colors.accent }}
                              />
                              <span className="font-medium text-[#1a1a2e]">{policy.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-neutral-600">{policy.annual_per_employee}</TableCell>
                          <TableCell className="text-right font-medium">{policy.total_allocated}</TableCell>
                          <TableCell className="text-right text-emerald-600 font-medium">{policy.total_used}</TableCell>
                          <TableCell className="text-right text-blue-600 font-medium">{policy.total_available}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full"
                                  style={{ 
                                    width: `${policy.utilization_rate}%`,
                                    backgroundColor: policy.color || colors.accent
                                  }}
                                />
                              </div>
                              <span className="text-xs text-neutral-500 w-10 text-right">{policy.utilization_rate}%</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </TabsContent>

        {/* ==================== EMPLOYEE BALANCES TAB ==================== */}
        <TabsContent value="allocations" className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button variant="outline" className="border-white/40">
              <Download className="w-4 h-4 mr-2" />
              Export to Excel
            </Button>
          </div>

          <div className="soft-card overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-white/40">
                    <TableHead className="sticky left-0 bg-white/40 z-10">Employee</TableHead>
                    {employeeBalances.policies?.map(p => {
                      const colors = getColorScheme(p.code);
                      return (
                        <TableHead key={p.code} className="text-center min-w-[80px]">
                          <div className="flex flex-col items-center gap-1">
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: p.color || colors.accent }}
                            />
                            <span className="text-xs font-medium">{p.code.toUpperCase()}</span>
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employeeBalances.employees
                    ?.filter(e => e.name.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((emp) => (
                      <TableRow key={emp.employee_id} className="hover:bg-white/60">
                        <TableCell className="sticky left-0 bg-white z-10">
                          <div>
                            <p className="font-medium text-[#1a1a2e]">{emp.name}</p>
                            <p className="text-xs text-neutral-500">{emp.department || 'No department'}</p>
                          </div>
                        </TableCell>
                        {employeeBalances.policies?.map(p => {
                          const bal = emp.balances[p.code] || {};
                          const isLow = bal.available < 2 && bal.allocated > 0;
                          const isNegative = bal.available < 0;
                          return (
                            <TableCell key={p.code} className="text-center">
                              <div className="flex flex-col items-center">
                                <span className={`font-semibold ${isNegative ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-[#1a1a2e]'}`}>
                                  {bal.available ?? '-'}
                                </span>
                                <span className="text-xs text-neutral-400">/ {bal.allocated ?? 0}</span>
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* ==================== MODALS ==================== */}
      
      {/* Policy Form Modal */}
      <PolicyFormModal
        open={showPolicyModal}
        onClose={() => { setShowPolicyModal(false); setEditingPolicy(null); }}
        policy={editingPolicy}
        onSave={handleSavePolicy}
      />

      {/* Holiday Form Modal */}
      <HolidayFormModal
        open={showHolidayModal}
        onClose={() => { setShowHolidayModal(false); setEditingHoliday(null); }}
        holiday={editingHoliday}
        holidayType={holidayType}
        setHolidayType={setHolidayType}
        year={holidayYear}
        onSave={handleSaveHoliday}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: '', data: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteConfirm.type === 'policy' ? 'Deactivate Leave Policy?' : 'Remove Holiday?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm.type === 'policy' 
                ? `This will deactivate the "${deleteConfirm.data}" leave policy. Employees will no longer be able to apply for this leave type.`
                : `This will remove "${deleteConfirm.data?.name}" (${deleteConfirm.data?.date}) from the holiday calendar.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline">Cancel</Button>
            </AlertDialogCancel>
            <Button
              onClick={() => {
                if (deleteConfirm.type === 'policy') {
                  handleDeletePolicy();
                } else {
                  handleDeleteHoliday();
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {deleteConfirm.type === 'policy' ? 'Deactivate' : 'Remove'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ==================== POLICY FORM MODAL ====================
function PolicyFormModal({ open, onClose, policy, onSave }) {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    annual_allocation: 0,
    carry_forward_allowed: false,
    max_carry_forward: 0,
    encashment_allowed: false,
    max_encashment: 0,
    requires_approval: true,
    auto_approved: false,
    sandwich_enabled: false,
    medical_cert_days: 0,
    advance_notice_days: 0,
    color: '#10B981',
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (policy) {
      setFormData({ ...formData, ...policy });
    } else {
      setFormData({
        code: '',
        name: '',
        description: '',
        annual_allocation: 0,
        carry_forward_allowed: false,
        max_carry_forward: 0,
        encashment_allowed: false,
        max_encashment: 0,
        requires_approval: true,
        auto_approved: false,
        sandwich_enabled: false,
        medical_cert_days: 0,
        advance_notice_days: 0,
        color: '#10B981',
        is_active: true
      });
    }
  }, [policy, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#1a1a2e]">{policy ? 'Edit Leave Policy' : 'Create Leave Policy'}</DialogTitle>
          <DialogDescription>
            Configure leave type settings, allocation, and rules
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-2">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-neutral-700 block mb-1.5">Policy Code *</label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s/g, '') })}
                placeholder="e.g., pl, sl, cl"
                disabled={!!policy}
                required
                className="bg-white"
              />
              <p className="text-xs text-neutral-400 mt-1">Unique identifier (cannot be changed)</p>
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 block mb-1.5">Policy Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Privilege Leave"
                required
                className="bg-white"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700 block mb-1.5">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the leave policy rules and conditions..."
              rows={2}
              className="bg-white"
            />
          </div>

          {/* Allocation Settings */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-neutral-700 block mb-1.5">Annual Days</label>
              <Input
                type="number"
                value={formData.annual_allocation}
                onChange={(e) => setFormData({ ...formData, annual_allocation: parseInt(e.target.value) || 0 })}
                min={0}
                className="bg-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 block mb-1.5">Advance Notice (days)</label>
              <Input
                type="number"
                value={formData.advance_notice_days}
                onChange={(e) => setFormData({ ...formData, advance_notice_days: parseInt(e.target.value) || 0 })}
                min={0}
                className="bg-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 block mb-1.5">Accent Color</label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-12 h-9 p-1 cursor-pointer"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="flex-1 bg-white font-mono text-sm"
                />
              </div>
            </div>
          </div>

          {/* Toggle Settings */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between p-3 bg-white/40 rounded-xl border border-white/40">
              <div>
                <p className="text-sm font-medium text-neutral-700">Carry Forward</p>
                <p className="text-xs text-neutral-500">Allow unused days to carry over</p>
              </div>
              <Switch
                checked={formData.carry_forward_allowed}
                onCheckedChange={(checked) => setFormData({ ...formData, carry_forward_allowed: checked })}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-white/40 rounded-xl border border-white/40">
              <div>
                <p className="text-sm font-medium text-neutral-700">Encashment</p>
                <p className="text-xs text-neutral-500">Allow converting to pay</p>
              </div>
              <Switch
                checked={formData.encashment_allowed}
                onCheckedChange={(checked) => setFormData({ ...formData, encashment_allowed: checked })}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-white/40 rounded-xl border border-white/40">
              <div>
                <p className="text-sm font-medium text-neutral-700">Requires Approval</p>
                <p className="text-xs text-neutral-500">Manager must approve</p>
              </div>
              <Switch
                checked={formData.requires_approval}
                onCheckedChange={(checked) => setFormData({ ...formData, requires_approval: checked })}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-white/40 rounded-xl border border-white/40">
              <div>
                <p className="text-sm font-medium text-neutral-700">Sandwich Rule</p>
                <p className="text-xs text-neutral-500">Include weekends in between</p>
              </div>
              <Switch
                checked={formData.sandwich_enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, sandwich_enabled: checked })}
              />
            </div>
          </div>

          {/* Conditional Fields */}
          {formData.carry_forward_allowed && (
            <div>
              <label className="text-sm font-medium text-neutral-700 block mb-1.5">Max Carry Forward Days</label>
              <Input
                type="number"
                value={formData.max_carry_forward}
                onChange={(e) => setFormData({ ...formData, max_carry_forward: parseInt(e.target.value) || 0 })}
                min={0}
                className="bg-white max-w-xs"
              />
            </div>
          )}

          {formData.encashment_allowed && (
            <div>
              <label className="text-sm font-medium text-neutral-700 block mb-1.5">Max Encashment Days</label>
              <Input
                type="number"
                value={formData.max_encashment}
                onChange={(e) => setFormData({ ...formData, max_encashment: parseInt(e.target.value) || 0 })}
                min={0}
                className="bg-white max-w-xs"
              />
            </div>
          )}

          <DialogFooter className="pt-4 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {policy ? 'Update Policy' : 'Create Policy'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ==================== HOLIDAY FORM MODAL ====================
function HolidayFormModal({ open, onClose, holiday, holidayType, setHolidayType, year, onSave }) {
  const [formData, setFormData] = useState({
    date: '',
    name: '',
    day: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (holiday) {
      setFormData({ date: holiday.date, name: holiday.name, day: holiday.day || '' });
    } else {
      setFormData({ date: '', name: '', day: '' });
    }
  }, [holiday, open]);

  // Auto-calculate day name
  useEffect(() => {
    if (formData.date) {
      const date = new Date(formData.date + 'T00:00:00');
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      setFormData(prev => ({ ...prev, day: days[date.getDay()] }));
    }
  }, [formData.date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#1a1a2e]">{holiday ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
          <DialogDescription>
            {holiday ? `Update this ${holiday.type} holiday in the ${year} calendar` : `Add a new holiday to the ${year} calendar`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2" data-testid="holiday-form">
          <div>
            <label className="text-sm font-medium text-neutral-700 block mb-1.5">Holiday Type *</label>
            <Select value={holiday ? holiday.type : holidayType} onValueChange={setHolidayType} disabled={!!holiday}>
              <SelectTrigger className="bg-white" data-testid="holiday-type-select">
                <SelectValue>{(holiday ? holiday.type : holidayType) === 'mandatory' ? 'Mandatory (Public Holiday)' : 'Optional (Restricted)'}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mandatory">
                  <div className="flex items-center gap-2">
                    <Flag className="w-4 h-4 text-indigo-600" />
                    Mandatory (Public Holiday)
                  </div>
                </SelectItem>
                <SelectItem value="optional">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    Optional (Restricted)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700 block mb-1.5">Date *</label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              className="bg-white"
              data-testid="holiday-date-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700 block mb-1.5">Holiday Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Republic Day, Diwali"
              required
              className="bg-white"
              data-testid="holiday-name-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700 block mb-1.5">Day of Week</label>
            <Input
              value={formData.day}
              readOnly
              className="bg-white/40 text-neutral-600"
              placeholder="Select a date first"
            />
          </div>

          <DialogFooter className="pt-4 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="holiday-form-submit">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {holiday ? 'Update Holiday' : 'Add Holiday'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
